import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, Card, Badge, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import Fade from 'react-reveal/Fade';
import { ThemeContext } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import AppContext from '../../AppContext';
import { getTranslation } from '../../constants/translations';
import '../../css/projectcard.css';

const styles = {
  badgeStyle: {
    padding: '5px 10px',
    margin: 5,
  },
  cardStyle: {
    borderRadius: 10,
  },
  cardTitleStyle: {
    fontSize: 24,
    fontWeight: 700,
  },
  cardTextStyle: {
    textAlign: 'left',
  },
  buttonStyle: {
    margin: 5,
  },
};

const renderMediaItems = (items, title, registerVideo) => {
  if (!items?.length) return null;

  return (
    <div className="project-card-media-grid">
      {items.map((item) => (
        <div key={item.src} className="project-card-media-item">
          {item.type === 'video' ? (
            <video
              className="project-card-video"
              controls
              preload="metadata"
              playsInline
              muted
              loop
              poster={item.poster}
              ref={registerVideo(item.src)}
              onClick={(event) => event.stopPropagation()}
            >
              <source src={item.src} type="video/mp4" />
            </video>
          ) : (
            <img
              className="project-card-inline-image"
              src={item.src}
              alt={item.alt || item.caption || title}
              onClick={(event) => event.stopPropagation()}
            />
          )}
          {item.caption && <div className="project-card-media-caption">{item.caption}</div>}
        </div>
      ))}
    </div>
  );
};

const ProjectCard = ({ project }) => {
  const theme = useContext(ThemeContext);
  const { language } = useContext(AppContext);
  const t = getTranslation(language).projects;
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const videoRefs = useRef(new Map());

  const registerVideo = (key) => (node) => {
    if (node) {
      videoRefs.current.set(key, node);
    } else {
      videoRefs.current.delete(key);
    }
  };

  if (!project) return null; // Avoid rendering if `project` is undefined

  const localizedProject =
    project.translationKey && t.items?.[project.translationKey]
      ? {
          ...project,
          ...t.items[project.translationKey],
          heroMedia: t.items[project.translationKey].heroMedia
            ? {
                ...project.heroMedia,
                ...t.items[project.translationKey].heroMedia,
              }
            : project.heroMedia,
          media: t.items[project.translationKey].media
            ? project.media?.map((item, index) => ({
                ...item,
                ...(t.items[project.translationKey].media[index] || {}),
              }))
            : project.media,
          mediaAfterIntro: t.items[project.translationKey].mediaAfterIntro
            ? project.mediaAfterIntro?.map((item, index) => ({
                ...item,
                ...(t.items[project.translationKey].mediaAfterIntro[index] || {}),
              }))
            : project.mediaAfterIntro,
          mediaAfterStack: t.items[project.translationKey].mediaAfterStack
            ? project.mediaAfterStack?.map((item, index) => ({
                ...item,
                ...(t.items[project.translationKey].mediaAfterStack[index] || {}),
              }))
            : project.mediaAfterStack,
          title: t.items[project.translationKey].title || project.title,
          introText: t.items[project.translationKey].introText || project.introText,
          stackText: t.items[project.translationKey].stackText || project.stackText,
          bodyText: t.items[project.translationKey].bodyText || project.bodyText,
        }
      : project;

  useEffect(() => {
    const syncMobile = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth <= 768);
      }
    };

    syncMobile();
    window.addEventListener('resize', syncMobile);

    return () => window.removeEventListener('resize', syncMobile);
  }, []);

  useEffect(() => {
    const videos = Array.from(videoRefs.current.values());
    if (!videos.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: [0.5, 0.75] }
    );

    videos.forEach((video) => observer.observe(video));

    return () => observer.disconnect();
  }, [localizedProject]);

  const isDark = theme.bsPrimaryVariant === 'dark';
  const cardPalette = isDark
    ? {
        '--project-card-surface': 'linear-gradient(180deg, rgba(16, 24, 33, 0.96) 0%, rgba(24, 36, 48, 0.94) 100%)',
        '--project-card-border': 'rgba(125, 211, 192, 0.16)',
        '--project-card-shadow': '0 22px 48px rgba(2, 6, 23, 0.34)',
        '--project-card-image-surface': 'linear-gradient(180deg, rgba(233, 239, 244, 0.08) 0%, rgba(148, 163, 184, 0.14) 100%)',
        '--project-card-footer': 'rgba(255, 255, 255, 0.04)',
      }
    : {
        '--project-card-surface': 'linear-gradient(180deg, rgba(255, 252, 247, 0.98) 0%, rgba(242, 236, 227, 0.95) 100%)',
        '--project-card-border': 'rgba(182, 148, 88, 0.18)',
        '--project-card-shadow': '0 20px 44px rgba(94, 78, 52, 0.12)',
        '--project-card-image-surface': 'linear-gradient(180deg, rgba(255, 255, 255, 0.92) 0%, rgba(245, 238, 227, 0.96) 100%)',
        '--project-card-footer': 'rgba(255, 255, 255, 0.56)',
      };

  return (
        <Fade bottom>
    
    <Col>
      <Card
        className={`project-card ${isExpanded ? 'is-expanded' : ''}`}
        style={{
          ...styles.cardStyle,
          ...cardPalette,
          background: 'var(--project-card-surface)',
          borderColor: 'var(--project-card-border)',
          boxShadow: 'var(--project-card-shadow)',
        }}
        text={theme.bsSecondaryVariant}
        onClick={() => {
          if (!isMobile) {
            setIsExpanded((value) => !value);
          }
        }}
        onMouseEnter={() => {
          if (!isMobile) {
            setIsExpanded(true);
          }
        }}
        onMouseLeave={() => {
          if (!isMobile) {
            setIsExpanded(false);
          }
        }}
        role={isMobile ? undefined : 'button'}
        tabIndex={isMobile ? undefined : 0}
        onKeyDown={(event) => {
          if (!isMobile && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            setIsExpanded((value) => !value);
          }
        }}
      >
        {localizedProject.heroMedia && (
          <div className="project-card-hero">
            {localizedProject.heroMedia.type === 'video' ? (
              <video
                className="project-card-hero-video"
                controls
                preload="metadata"
                playsInline
                muted
                loop
                poster={localizedProject.heroMedia.poster}
                ref={registerVideo(localizedProject.heroMedia.src)}
                onClick={(event) => event.stopPropagation()}
              >
                <source src={localizedProject.heroMedia.src} type="video/mp4" />
              </video>
            ) : (
              <img
                className="project-card-hero-image"
                src={localizedProject.heroMedia.src}
                alt={localizedProject.heroMedia.alt || localizedProject.title}
                onClick={(event) => event.stopPropagation()}
              />
            )}
            {localizedProject.heroMedia.caption && (
              <div className="project-card-media-caption">{localizedProject.heroMedia.caption}</div>
            )}
          </div>
        )}
        {localizedProject.image && !localizedProject.heroMedia && (
          <Card.Img
            className="project-card-image"
            variant="top"
            src={localizedProject.image}
            style={localizedProject.customImageStyle || { width: '100%', height: 'auto' }}
          />
        )}
        <Card.Body className="project-card-main">
          <Card.Title className="project-card-title" style={styles.cardTitleStyle}>{localizedProject.title}</Card.Title>
          <div className="project-card-expandable">
            {localizedProject.introText && (
              <Card.Text className="project-card-text" style={styles.cardTextStyle}>
                <ReactMarkdown>{localizedProject.introText}</ReactMarkdown>
              </Card.Text>
            )}
            {renderMediaItems(localizedProject.mediaAfterIntro, localizedProject.title, registerVideo)}
            {localizedProject.stackText && (
              <Card.Text className="project-card-text" style={styles.cardTextStyle}>
                <ReactMarkdown>{localizedProject.stackText}</ReactMarkdown>
              </Card.Text>
            )}
            {renderMediaItems(localizedProject.mediaAfterStack, localizedProject.title, registerVideo)}
            <Card.Text className="project-card-text" style={styles.cardTextStyle}>
              <ReactMarkdown>{localizedProject.bodyText || t.noDescription}</ReactMarkdown>
            </Card.Text>
            {renderMediaItems(localizedProject.media, localizedProject.title, registerVideo)}
          </div>
        </Card.Body>
        {localizedProject.links && (
          <Card.Body className="project-card-links">
            {localizedProject.links.map((link) => (
              <Button
                key={link.href}
                className="project-card-link-button"
                style={styles.buttonStyle}
                variant={`outline-${theme.bsSecondaryVariant}`}
                onClick={(event) => {
                  event.stopPropagation();
                  window.open(link.href, '_blank');
                }}
              >
                {link.text === 'Visit Website' ? t.visitWebsite : link.text}
              </Button>
            ))}
          </Card.Body>
        )}
        {localizedProject.tags && (
          <Card.Footer style={{ backgroundColor: 'var(--project-card-footer)' }}>
            {localizedProject.tags.map((tag) => (
              <Badge
                key={tag}
                pill
                bg={theme.bsSecondaryVariant}
                text={theme.bsPrimaryVariant}
                style={styles.badgeStyle}
              >
                {tag}
              </Badge>
            ))}
          </Card.Footer>
        )}
      </Card>
    </Col>
    </Fade>
  );
};

ProjectCard.propTypes = {
  project: PropTypes.shape({
    title: PropTypes.string.isRequired,
    bodyText: PropTypes.string,
    image: PropTypes.string,
    heroMedia: PropTypes.shape({
      type: PropTypes.oneOf(['image', 'video']).isRequired,
      src: PropTypes.string.isRequired,
      caption: PropTypes.string,
      alt: PropTypes.string,
      poster: PropTypes.string,
    }),
    media: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(['image', 'video']).isRequired,
        src: PropTypes.string.isRequired,
        caption: PropTypes.string,
        alt: PropTypes.string,
        poster: PropTypes.string,
      })
    ),
    mediaAfterIntro: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(['image', 'video']).isRequired,
        src: PropTypes.string.isRequired,
        caption: PropTypes.string,
        alt: PropTypes.string,
        poster: PropTypes.string,
      })
    ),
    mediaAfterStack: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(['image', 'video']).isRequired,
        src: PropTypes.string.isRequired,
        caption: PropTypes.string,
        alt: PropTypes.string,
        poster: PropTypes.string,
      })
    ),
    introText: PropTypes.string,
    stackText: PropTypes.string,
    customImageStyle: PropTypes.object,
    translationKey: PropTypes.string,
    links: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        href: PropTypes.string.isRequired,
      })
    ),
    tags: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

export default ProjectCard;
